/**
 * ContainerScreen: the shared click engine behind the inventory, crafting table,
 * furnace, chest and creative screens. A screen is a list of `SlotDef`s (a stack
 * object + vanilla GUI coordinates) and the engine implements vanilla's
 * AbstractContainerMenu semantics on them: left/right pickup, shift quick-move,
 * double-click gather, drag-split, number-key swap, Q drop, click-outside drop.
 *
 * Coordinates are GUI px relative to the panel's top-left; slot (x, y) is the
 * 16x16 item origin, exactly as vanilla lists them.
 */

import { emptyStack, isEmpty, type Stack } from '../../game/types';
import {
  CLICK, SLOT_OUTSIDE, canPlace, clickSlot, dragAccepts, dragPlan, finishDrag, giveBack, maxFor, moveTo, outsideClick, pickupAll, quickMove,
  roomFor, same, swapSlot, throwSlot, type ClickArgs, type Menu, type MenuCtx, type MenuEnv, type MenuSlot,
} from '../../game/menu';
import { statTooltipLines } from '../tooltipStats';
import { enchTooltipLines } from '../../game/enchant/stack';
import { itemDef } from '../../game/itemdb';
import { stackName } from '../../items';
import { isPotionItem, potionTooltipLines } from '../../game/brewing/potions';
import { I } from '../../game/itemIds';
import { ItemIcons } from '../itemIcons';
import { GuiStage, Label, sprite, Tooltip } from '../kit';
import type { ScreenDeps } from './screenHost';

const ENCHANTED_BOOK = I.ENCHANTED_BOOK;

export interface SlotDef extends MenuSlot {
  x: number; y: number;
  /** Sprite (relative to mc/) shown while the slot is empty (armor silhouettes). */
  emptyIcon?: string;
  // --- runtime ---
  el?: HTMLDivElement;
  iconEl?: HTMLDivElement;
  hlBack?: HTMLDivElement;
  hlFront?: HTMLDivElement;
  emptyEl?: HTMLDivElement;
}

export { same };

export abstract class ContainerScreen {
  readonly root: HTMLDivElement;
  protected readonly slots: SlotDef[] = [];
  readonly cursor: Stack = emptyStack();
  /** Panel size in GUI px (vanilla imageWidth / imageHeight). */
  protected w = 176;
  protected h = 166;
  protected left = 0;
  protected top = 0;
  protected hover: SlotDef | null = null;
  protected mx = 0; protected my = 0;
  protected cx = 0; protected cy = 0; // client px of the pointer
  private readonly cursorEl: HTMLDivElement;
  private lastClick = { slot: null as SlotDef | null, time: 0, button: -1 };
  private drag: { button: 0 | 1; start: SlotDef; slots: SlotDef[]; moved: boolean } | null = null;
  protected onCloseCb: (() => void) | null = null;
  private readonly labels: Label[] = [];
  /** The click rules' view of this screen: its own slot list, or the shared menu given to `use()`. */
  protected menu: MenuCtx;
  /** `menu` came from a game/menu.ts builder: slot indices are the wire's, so clicks can be reported to a server. */
  private shared: Menu | null = null;

  constructor(protected readonly deps: ScreenDeps, protected readonly stage: GuiStage) {
    this.menu = {
      slots: this.slots, cursor: this.cursor, inventory: deps.inventory,
      dropStack: (s) => { this.deps.dropStack(s); },
      quickMoveStack: (i) => { this.quickMoveStack(i); },
      resultTarget: () => this.resultTarget(),
      clickSource: (s, b) => { this.clickSource(s as SlotDef, b); },
      quickMoveSource: (s) => { this.quickMoveSource(s as SlotDef); },
    };
    this.root = document.createElement('div');
    this.root.style.cssText = 'position:absolute;pointer-events:none;';
    this.cursorEl = document.createElement('div');
    this.cursorEl.style.cssText = 'position:absolute;left:0;top:0;width:16px;height:16px;pointer-events:none;z-index:300;';
  }

  // ---------------------------------------------------------------- setup ----

  /** Attach panel + slot elements. Call once from the subclass constructor after filling `slots`. */
  protected mount(): void {
    for (const s of this.slots) this.buildSlotEl(s);
    this.root.style.width = this.w + 'px';
    this.root.style.height = this.h + 'px';
  }

  protected buildSlotEl(s: SlotDef): void {
    const el = document.createElement('div');
    el.style.cssText = `position:absolute;left:${s.x}px;top:${s.y}px;width:16px;height:16px;pointer-events:none;`;
    if (s.emptyIcon) {
      s.emptyEl = sprite(s.emptyIcon, 0, 0, 16, 16);
      el.appendChild(s.emptyEl);
    }
    s.hlBack = sprite('gui/sprites/container/slot_highlight_back.png', -4, -4, 24, 24);
    s.hlFront = sprite('gui/sprites/container/slot_highlight_front.png', -4, -4, 24, 24);
    s.hlBack.style.display = s.hlFront.style.display = 'none';
    s.iconEl = document.createElement('div');
    s.iconEl.style.cssText = 'position:absolute;left:0;top:0;width:16px;height:16px;';
    el.append(s.hlBack, s.iconEl, s.hlFront);
    s.el = el;
    this.root.appendChild(el);
  }

  protected addLabel(text: string, x: number, y: number, align: 'left' | 'center' | 'right' = 'left'): Label {
    const l = new Label(text, x, y, { color: '#404040', shadow: false, align });
    this.labels.push(l);
    this.root.appendChild(l.el);
    return l;
  }

  /** Slots for a block of the player's inventory: 27 main (inventory.slots 9..35) then 9 hotbar. */
  protected playerSlots(mainY: number, hotbarY: number): { main: SlotDef[]; hotbar: SlotDef[] } {
    const inv = this.deps.inventory;
    const main: SlotDef[] = [], hotbar: SlotDef[] = [];
    for (let r = 0; r < 3; r++) {
      for (let c = 0; c < 9; c++) main.push({ stack: inv.slots[9 + r * 9 + c], x: 8 + c * 18, y: mainY + r * 18, tag: 'main' });
    }
    for (let c = 0; c < 9; c++) hotbar.push({ stack: inv.slots[c], x: 8 + c * 18, y: hotbarY, tag: 'hotbar' });
    return { main, hotbar };
  }

  /** Adopt a shared menu: its slots become this screen's (same objects, same order); place them with at() / placePlayer(). */
  protected use<M extends Menu>(menu: M): M {
    menu.cursor = this.cursor;
    this.menu = menu;
    this.shared = menu;
    for (const s of menu.slots) this.slots.push(Object.assign(s, { x: 0, y: 0 }));
    return menu;
  }
  /** The shared menu behind this screen (null for screens with their own slot list). */
  get sharedMenu(): Menu | null { return this.shared; }
  protected at(i: number, x: number, y: number, extra?: Partial<SlotDef>): void {
    Object.assign(this.slots[i], { x, y }, extra);
  }
  /** Position the 27 main + 9 hotbar slots of a shared menu, starting at slot `start`. */
  protected placePlayer(start: number, mainY: number, hotbarY: number): void {
    for (let i = 0; i < 27; i++) this.at(start + i, 8 + (i % 9) * 18, mainY + Math.floor(i / 9) * 18);
    for (let c = 0; c < 9; c++) this.at(start + 27 + c, 8 + c * 18, hotbarY);
  }
  /** What a shared menu needs of the player, from this screen's deps (read at call time: server mode swaps them). */
  protected env(over?: Partial<MenuEnv>): MenuEnv {
    return {
      inventory: this.deps.inventory,
      creative: () => this.deps.gameMode() === 'creative',
      dropStack: (s) => { this.deps.dropStack(s); },
      addXp: (n) => { this.deps.addXp?.(n); },
      level: () => 0, spendLevels: () => { /* screens with a cost pass their own */ },
      rnd: Math.random,
      ...over,
    };
  }
  /** Tell the server about a click this screen just applied as a prediction (server mode only). */
  protected report(slot: SlotDef | null, btn: number, mode: number, extra?: Partial<ClickArgs>): void {
    if (!this.shared || !this.deps.remote) return;
    this.deps.remote({ slot: slot ? this.shared.slots.indexOf(slot) : SLOT_OUTSIDE, btn, mode, ...extra });
  }

  // -------------------------------------------------------------- layout ----

  layout(stageW: number, stageH: number): void {
    this.left = Math.floor((stageW - this.w) / 2);
    this.top = Math.floor((stageH - this.h) / 2);
    this.root.style.left = this.left + 'px';
    this.root.style.top = this.top + 'px';
    this.onLayout();
  }
  protected onLayout(): void { /* subclasses */ }

  mountInto(parent: HTMLElement): void {
    parent.appendChild(this.root);
    parent.appendChild(this.cursorEl);
    this.refresh(true);
  }

  unmount(): void {
    this.root.remove();
    this.cursorEl.remove();
    Tooltip.hide();
  }

  // ------------------------------------------------------------- helpers ----

  protected maxFor(slot: SlotDef, s: Stack): number { return maxFor(slot, s); }
  protected canPlace(slot: SlotDef, s: Stack): boolean { return canPlace(slot, s); }
  protected changed(...touched: (SlotDef | undefined)[]): void {
    for (const s of touched) s?.onChanged?.();
    this.deps.onChange();
  }

  /** Give a stack back to the player's inventory, dropping what does not fit. */
  protected giveBack(s: Stack): void { giveBack(this.menu, s); }
  /** vanilla Container#moveItemStackTo over slots[start, end). */
  protected moveTo(stack: Stack, start: number, end: number, reverse: boolean): boolean { return moveTo(this.menu, stack, start, end, reverse); }
  /** How many of `s` would fit into slots[start,end) (dry run of moveTo). */
  protected roomFor(s: Stack, start: number, end: number): number { return roomFor(this.menu, s, start, end); }

  // ------------------------------------------------- per-screen behaviour ----

  /** Shift-click routing for a non-result slot: move slots[i].stack somewhere sensible. */
  protected quickMoveStack(_i: number): void { /* screens with their own slot list */ }
  /** Where a shift-clicked crafting/furnace result goes: [start, end, reverse]. */
  protected resultTarget(): [number, number, boolean] { return [0, 0, false]; }
  /** Called when the screen closes (return crafting grids etc.). */
  protected onClose(): void { this.shared?.close(); }
  /** Hook for subclasses with extra keys (creative search typing). */
  keyDown(_e: KeyboardEvent): boolean { return false; }
  /** Per-frame work (furnace progress, preview). */
  update(_dt: number): void { /* subclasses */ }

  /** A drag-split is in progress (its preview counts are not in the slots yet). */
  get dragging(): boolean { return this.drag !== null; }

  close(): void {
    if (this.drag) this.drag = null;
    this.onClose();
    this.giveBack(this.cursor);
    this.deps.onChange();
    this.unmount();
  }

  // --------------------------------------------------------------- clicks ----

  protected slotAt(mx: number, my: number): SlotDef | null {
    const lx = mx - this.left, ly = my - this.top;
    for (const s of this.slots) {
      if (lx >= s.x && lx < s.x + 16 && ly >= s.y && ly < s.y + 16) return s;
    }
    return null;
  }
  /** Click outside the panel (vanilla hasClickedOutside). */
  protected outside(mx: number, my: number): boolean {
    return mx < this.left || my < this.top || mx >= this.left + this.w || my >= this.top + this.h;
  }
  protected onOutsideClick(button: number): void {
    if (isEmpty(this.cursor)) return;
    outsideClick(this.menu, button);
    this.report(null, button, CLICK.PICKUP);
    this.changed();
  }

  pointerMove(clientX: number, clientY: number): void {
    const S = this.stage.scale;
    this.cx = clientX; this.cy = clientY;
    this.mx = clientX / S; this.my = clientY / S;
    this.hover = this.slotAt(this.mx, this.my);
    const d = this.drag;
    if (d && this.hover && this.hover !== d.slots[d.slots.length - 1] && !d.slots.includes(this.hover)) {
      const s = this.hover;
      const ok = dragAccepts(this.menu, s) && this.cursor.count > d.slots.length;
      if (ok) { d.slots.push(s); if (d.slots.length > 1) d.moved = true; }
    }
    this.refresh(false);
  }

  pointerDown(button: number, shift: boolean): void {
    if (button !== 0 && button !== 1) return;
    const slot = this.slotAt(this.mx, this.my);
    if (!slot) {
      if (this.outside(this.mx, this.my)) this.onOutsideClick(button);
      return;
    }
    const now = performance.now();
    const dbl = this.lastClick.slot === slot && this.lastClick.button === button && now - this.lastClick.time < 250;
    this.lastClick = { slot, time: now, button };
    if (shift) { this.quickMove(slot); return; }
    if (dbl && button === 0 && !isEmpty(this.cursor) && (isEmpty(slot.stack) || slot.result)) {
      pickupAll(this.menu, slot, button);
      this.report(slot, button, CLICK.PICKUP_ALL);
      this.deps.onChange();
      this.lastClick.time = 0;
      return;
    }
    if (!isEmpty(this.cursor) && !slot.result && !slot.source && this.canPlace(slot, this.cursor)) {
      // tentative drag: decided on release
      this.drag = { button, start: slot, slots: [slot], moved: false };
      if (!(isEmpty(slot.stack) || same(slot.stack, this.cursor))) this.drag.slots = [];
      this.refresh(false);
      return;
    }
    this.click(slot, button);
  }

  pointerUp(button: number): void {
    const d = this.drag;
    if (!d || d.button !== button) return;
    this.drag = null;
    if (d.slots.length >= 2) this.finishDrag(d.slots, d.button);
    else this.click(d.start, d.button);
  }

  /** vanilla QUICK_CRAFT (drag-split): left = even split, right = one each. */
  private finishDrag(slots: SlotDef[], button: 0 | 1): void {
    const idx = slots.map((s) => this.menu.slots.indexOf(s));
    finishDrag(this.menu, slots, button);
    this.report(slots[0], button, CLICK.DRAG, { slots: idx });
    this.deps.onChange();
    this.refresh(false);
  }

  /** The normal (non-drag) vanilla PICKUP click on a slot. */
  protected click(slot: SlotDef, button: number): void {
    clickSlot(this.menu, slot, button);
    if (slot.source) return;
    this.report(slot, button, CLICK.PICKUP);
    this.deps.onChange();
  }

  /** Creative item-list slots; overridden by the creative screen. */
  protected clickSource(_slot: SlotDef, _button: number): void { /* creative only */ }

  /** Shift-click. */
  protected quickMove(slot: SlotDef): void {
    quickMove(this.menu, slot);
    if (slot.source) return;
    this.report(slot, 0, CLICK.QUICK_MOVE);
    this.deps.onChange();
  }
  protected quickMoveSource(_slot: SlotDef): void { /* creative only */ }

  // ---------------------------------------------------------------- keys ----

  /** Number keys 1..9: swap the hovered slot with that hotbar slot. */
  hotbarSwap(n: number): boolean {
    const slot = this.hover;
    if (!slot || slot.source) return false;
    swapSlot(this.menu, slot, this.deps.inventory.slots[n]);
    this.report(slot, n, CLICK.SWAP);
    this.deps.onChange();
    this.refresh(false);
    return true;
  }

  /** Q over a slot: drop one (ctrl: the whole stack). */
  dropHovered(whole: boolean): boolean {
    const slot = this.hover;
    if (!slot || !throwSlot(this.menu, slot, whole)) return false;
    this.report(slot, whole ? 1 : 0, CLICK.THROW);
    this.deps.onChange();
    this.refresh(false);
    return true;
  }

  // ------------------------------------------------------------- display ----

  /** Repaint slot icons, hover highlight, tooltip and the cursor stack. */
  refresh(force: boolean): void {
    const plan = this.drag && this.drag.slots.length > 1 ? dragPlan(this.menu, this.drag.slots, this.drag.button) : null;
    for (const s of this.slots) {
      if (!s.iconEl) continue;
      let shown = s.stack;
      const pc = plan?.counts.get(s);
      if (pc !== undefined) shown = { ...this.cursor, count: pc };
      if (force) delete s.iconEl.dataset.k;
      ItemIcons.decorate(s.iconEl, shown);
      if (s.emptyEl) s.emptyEl.style.display = isEmpty(shown) ? '' : 'none';
      const hl = s === this.hover || (plan ? plan.counts.has(s) : false);
      const disp = hl ? '' : 'none';
      if (s.hlBack!.style.display !== disp) { s.hlBack!.style.display = disp; s.hlFront!.style.display = disp; }
    }
    // cursor stack, drawn at the pointer (vanilla: item origin = mouse - 8)
    const shownCursor = plan ? { ...this.cursor, count: plan.remaining } : this.cursor;
    ItemIcons.decorate(this.cursorEl, shownCursor);
    this.cursorEl.style.transform = `translate(${Math.round(this.mx - 8)}px,${Math.round(this.my - 8)}px)`;
    const tip = this.tipLines();
    if (tip) Tooltip.show(tip, this.cx, this.cy); else Tooltip.hide();
  }

  /** Tooltip text for the pointer position, or null: item name while the cursor is empty (vanilla). */
  protected tipLines(): string[] | null {
    const h = this.hover;
    if (h && isEmpty(this.cursor) && !isEmpty(h.stack) && !this.drag) {
      const nm = stackName(h.stack) || itemDef(h.stack.id).name;
      if (!nm) return null;
      const extra = isPotionItem(h.stack.id) ? potionTooltipLines(h.stack.id, h.stack.dmg) : [...enchTooltipLines(h.stack), ...statTooltipLines(h.stack)];
      // vanilla: an enchanted item's name turns aqua
      const it = h.stack.name ? '\u00a7o' : '';
      return h.stack.ench?.length && h.stack.id !== ENCHANTED_BOOK ? [it + '\u00a7b' + nm, ...extra] : [it + nm, ...extra];
    }
    return null;
  }
}
